import CloseIcon from '@mui/icons-material/Close';
import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useFilterSettingsStore } from '../..';
import { useConditionallyClosable } from '../../hooks/useClosable';
import { ICompanyFilterSettings, IDealFilterSettings, ILeadFilterSettings, ILeaseCompFilterSettings, ISaleCompFilterSettings, ITenantInMarketFilterSettings } from '../../interfaces/IFilterSettings';
import { ClosableOrder } from '../../store/ClosableStore';
import { GlobalFiltersTab } from '../../store/FilterSettingsStore';
import { areCompanyFiltersDifferent, areDealFiltersDifferent, areLeadFiltersDifferent, areLeaseCompFiltersDifferent, areSaleCompFiltersDifferent, areTenantInMarketFiltersDifferent } from '../../utils/filterSettingsUtils';
import commonStyles from '../Common.module.css';
import EditControls from '../EditControls';
import Modal from '../Modal';
import SegmentedButtons from '../SegmentedButtons';
import styles from './GlobalFilters.module.css';
import GlobalFiltersCompaniesTab from './GlobalFiltersCompaniesTab';
import GlobalFiltersDealsTab from './GlobalFiltersDealsTab';
import GlobalFiltersLeadsTab from './GlobalFiltersLeadsTab';
import GlobalFiltersLeaseCompsTab from './GlobalFiltersLeaseCompsTab';
import GlobalFiltersSaleCompsTab from './GlobalFiltersSaleCompsTab';
import GlobalFiltersTenantsInMarketTab from './GlobalFiltersTenantsInMarketTab';

function GlobalFilters() {
    const filterSettingsStore = useFilterSettingsStore();
    const [dealFiltersState, setDealFiltersState] = useState<IDealFilterSettings>(useMemo(() => ({ ...filterSettingsStore.filters.dealSettings }), []));
    const [leadFiltersState, setLeadFiltersState] = useState<ILeadFilterSettings>(useMemo(() => ({ ...filterSettingsStore.filters.leadSettings }), []));
    const [leaseCompFiltersState, setLeaseCompFiltersState] = useState<ILeaseCompFilterSettings>(useMemo(() => ({ ...filterSettingsStore.filters.leaseCompSettings }), []));
    const [saleCompFiltersState, setSaleCompFiltersState] = useState<ISaleCompFilterSettings>(useMemo(() => ({ ...filterSettingsStore.filters.saleCompSettings }), []));
    const [tenantInMarketFiltersState, setTenantInMarketFiltersState] = useState<ITenantInMarketFilterSettings>(useMemo(() => ({ ...filterSettingsStore.filters.tenantInMarketSettings }), []));
    const [companyFiltersState, setCompanyFiltersState] = useState<ICompanyFilterSettings>(useMemo(() => ({ ...filterSettingsStore.filters.companySettings }), []));
    const isStateChanged = useMemo(() => computed(() =>
        areDealFiltersDifferent(dealFiltersState, filterSettingsStore.filters.dealSettings) ||
        areLeadFiltersDifferent(leadFiltersState, filterSettingsStore.filters.leadSettings) ||
        areLeaseCompFiltersDifferent(leaseCompFiltersState, filterSettingsStore.filters.leaseCompSettings) ||
        areSaleCompFiltersDifferent(saleCompFiltersState, filterSettingsStore.filters.saleCompSettings) ||
        areTenantInMarketFiltersDifferent(tenantInMarketFiltersState, filterSettingsStore.filters.tenantInMarketSettings) ||
        areCompanyFiltersDifferent(companyFiltersState, filterSettingsStore.filters.companySettings)
    ), [dealFiltersState, leadFiltersState, leaseCompFiltersState, saleCompFiltersState, tenantInMarketFiltersState]).get();
    const segmentedButtons = useMemo(() => [
        { value: GlobalFiltersTab.Deals, label: "Deals" },
        { value: GlobalFiltersTab.Leads, label: "Leads" },
        { value: GlobalFiltersTab.LeaseComps, label: "Lease Comps" },
        { value: GlobalFiltersTab.SaleComps, label: "Sale Comps" },
        { value: GlobalFiltersTab.TIMs, label: "Tenants in Market" },
        { value: GlobalFiltersTab.Companies, label: "Companies" },
    ], []);

    const cancel = () => filterSettingsStore.close();
    useConditionallyClosable(ClosableOrder.High, cancel, filterSettingsStore.isOpen);
    const apply = () => {
        filterSettingsStore.setFilters({
            dealSettings: { ...dealFiltersState },
            leadSettings: { ...leadFiltersState },
            leaseCompSettings: { ...leaseCompFiltersState },
            saleCompSettings: { ...saleCompFiltersState },
            tenantInMarketSettings: { ...tenantInMarketFiltersState },
            companySettings: { ...companyFiltersState },
        });

        cancel();
    };

    const setDefaults = () => {
        filterSettingsStore.setDefaultFilters({
            dealSettings: { ...dealFiltersState },
            leadSettings: { ...leadFiltersState },
            leaseCompSettings: { ...leaseCompFiltersState },
            saleCompSettings: { ...saleCompFiltersState },
            tenantInMarketSettings: { ...tenantInMarketFiltersState },
            companySettings: { ...companyFiltersState },
        });

        cancel();
    };

    const resetFilters = () => {
        filterSettingsStore.setFilters({ ...filterSettingsStore.defaultFilters });
        cancel();
    };

    useEffect(() => {
        if (!filterSettingsStore.isOpen) {
            setDealFiltersState({ ...filterSettingsStore.filters.dealSettings });
            setLeadFiltersState({ ...filterSettingsStore.filters.leadSettings });
            setLeaseCompFiltersState({ ...filterSettingsStore.filters.leaseCompSettings });
            setSaleCompFiltersState({ ...filterSettingsStore.filters.saleCompSettings });
            setTenantInMarketFiltersState({ ...filterSettingsStore.filters.tenantInMarketSettings });
            setCompanyFiltersState({ ...filterSettingsStore.filters.companySettings });
        }
    }, [filterSettingsStore.isOpen]);

    if (!filterSettingsStore.isOpen)
        return null;

    return (
        <Modal className={styles.container} onClose={cancel}>
            <div className={styles.header}>
                <div>Filter</div>
                <CloseIcon className={commonStyles.clickableIcon} onClick={cancel} />
            </div>
            <SegmentedButtons
                value={filterSettingsStore.tab}
                onChange={value => filterSettingsStore.open(value)}
                options={segmentedButtons}
                isTabSwitchDisabled
            />
            {filterSettingsStore.tab === GlobalFiltersTab.Deals && <GlobalFiltersDealsTab dealFiltersState={dealFiltersState} setDealFiltersState={setDealFiltersState} />}
            {filterSettingsStore.tab === GlobalFiltersTab.Leads && <GlobalFiltersLeadsTab leadFiltersState={leadFiltersState} setLeadFiltersState={setLeadFiltersState} />}
            {filterSettingsStore.tab === GlobalFiltersTab.LeaseComps && <GlobalFiltersLeaseCompsTab leaseCompFiltersState={leaseCompFiltersState} setLeaseCompFiltersState={setLeaseCompFiltersState} />}
            {filterSettingsStore.tab === GlobalFiltersTab.SaleComps && <GlobalFiltersSaleCompsTab saleCompFiltersState={saleCompFiltersState} setSaleCompFiltersState={setSaleCompFiltersState} />}
            {filterSettingsStore.tab === GlobalFiltersTab.TIMs && <GlobalFiltersTenantsInMarketTab tenantInMarketFiltersState={tenantInMarketFiltersState} setTenantInMarketFiltersState={setTenantInMarketFiltersState} />}
            {filterSettingsStore.tab === GlobalFiltersTab.Companies && <GlobalFiltersCompaniesTab companyFiltersState={companyFiltersState} setCompanyFiltersState={setCompanyFiltersState} />}
            <div className={[commonStyles.controls, styles.controls].join(" ")}>
                <div className={commonStyles.controlGroup}>
                    <EditControls
                        isStateChanged={isStateChanged}
                        isUpdateLoading={false}
                        onUpdate={apply}
                        onCancel={cancel}
                    />
                </div>
                <div className={commonStyles.controlGroup}>
                    <button className={commonStyles.button} onClick={resetFilters}>Reset filters</button>
                    <button className={commonStyles.button} onClick={setDefaults}>Set defaults</button>
                </div>
            </div>
        </Modal>
    );
};

export default observer(GlobalFilters);